import { useEffect, useState } from 'react'
import { canDeliverShortcutFile } from '../utils/desktopShortcut'

// Not in the DOM lib types — Chrome/Edge/Android-specific event, no W3C spec.
interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

declare global {
  interface Window {
    /** Set by the inline capture script in index.html — see the comment there. */
    __deferredInstallPrompt?: BeforeInstallPromptEvent | null
  }
}

/**
 * A visual, step-by-step guide to the browser's own install control.
 *
 * Safari and Firefox expose no install API (verified: `onbeforeinstallprompt` is absent from
 * `window` in both WebKit and Gecko), and the menu holding "Add to Home Screen" is browser chrome
 * that script cannot open. Since the button physically cannot perform the install there, the next
 * best thing is to point straight at the control the user has to press — which glyph to look for,
 * where on screen it lives, and what to pick from the menu.
 */
export interface InstallGuide {
  /** Which control the user must open: the share sheet, or the browser's overflow menu. */
  control: 'share' | 'menu'
  /** Where that control physically sits, so the overlay can point at the right screen edge. */
  location: 'bottom-bar' | 'top-right'
  /** The entry to choose once the control is open, rendered as it appears in the menu. */
  menuItem: string
  /** "Tap" on touch devices, "Click" on desktop — the overlay reads as instructions, so the verb
   *  has to match the input the reader actually has. */
  verb: 'Tap' | 'Click'
  /** Where the control lives, in words — also the accessible description of the pointer. */
  where: string
}

export interface ManualInstallInstructions {
  title: string
  steps: string
  /** Present when we can show a guided overlay instead of a paragraph of prose. */
  guide?: InstallGuide
  /**
   * Whether to offer the "download a desktop shortcut" action alongside the steps. True only on
   * non-Chromium desktop: those browsers can't be installed from script, and a downloaded
   * shortcut file is the one real action available there instead of a "press ⌘D" instruction.
   * Chromium is excluded because it can genuinely install, and phones have nowhere to put the
   * file.
   */
  canDownloadShortcut?: boolean
}

// iPadOS 13+ reports its UA as a desktop Mac — maxTouchPoints is the standard way to tell it
// apart from an actual Mac, which reports 0 (or 1 for some trackpads, never iPad's 5).
function isIosDevice(): boolean {
  if (typeof navigator === 'undefined') return false
  const ua = navigator.userAgent
  return /iPad|iPhone|iPod/.test(ua) || (ua.includes('Macintosh') && navigator.maxTouchPoints > 1)
}

// Chrome/Firefox/Edge on iOS are all still forced onto Safari's WebKit under the hood but
// identify themselves in the UA string — exclude them so we don't show Safari-specific
// "Add to Home Screen" steps to a browser with a different (or no) share-sheet flow.
function isIosSafari(): boolean {
  if (typeof navigator === 'undefined') return false
  const ua = navigator.userAgent
  return isIosDevice() && /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua)
}

function isMacSafari(): boolean {
  if (typeof navigator === 'undefined') return false
  const ua = navigator.userAgent
  return !isIosDevice() && /Macintosh/.test(ua) && /Safari/.test(ua) && !/Chrome|Chromium|Edg|OPR/.test(ua)
}

/** iPad keeps Safari's toolbar at the top; iPhone moves it to the bottom of the screen. */
function isIpad(): boolean {
  if (typeof navigator === 'undefined') return false
  const ua = navigator.userAgent
  return /iPad/.test(ua) || (ua.includes('Macintosh') && navigator.maxTouchPoints > 1)
}

function isAndroid(): boolean {
  if (typeof navigator === 'undefined') return false
  return /Android/.test(navigator.userAgent)
}

function isChromiumDesktop(): boolean {
  if (typeof navigator === 'undefined') return false
  const ua = navigator.userAgent
  return /Chrome|Chromium|Edg/.test(ua) && !/Android/.test(ua) && !isIosDevice()
}

/** Desktop Firefox specifically. Mozilla removed its site-specific-browser ("Install site as
 *  app") support and never shipped it on macOS/Linux, so desktop Firefox cannot install a PWA at
 *  all — no `beforeinstallprompt`, and no install entry in its menus either. Firefox on Android
 *  is unaffected (it installs via the ⋮ menu) and is matched by isAndroid() before this. */
function isFirefoxDesktop(): boolean {
  if (typeof navigator === 'undefined') return false
  const ua = navigator.userAgent
  return /Firefox/.test(ua) && !/Android/.test(ua) && !isIosDevice()
}

/** Only desktops have somewhere to put a downloaded shortcut file (Desktop, Dock, taskbar). */
function isDesktop(): boolean {
  return !isAndroid() && !isIosDevice()
}

/**
 * Whether to offer the "download a shortcut" action: only where there is somewhere to put the
 * file AND the browser will actually let it land intact. Firefox mangles `.url`/`.desktop` into
 * `.url.download`/`.desktop.download`, so on Firefox/Windows and Firefox/Linux the button would
 * hand over a file that does nothing — see canDeliverShortcutFile.
 */
function offerShortcutDownload(): boolean {
  return isDesktop() && canDeliverShortcutFile()
}

function isStandalone(): boolean {
  if (typeof window === 'undefined') return false
  return (
    // matchMedia is unavailable in some test/legacy environments (e.g. jsdom without a mock) —
    // guard rather than throw and fall through to the iOS-specific flag below.
    (typeof window.matchMedia === 'function' && window.matchMedia('(display-mode: standalone)').matches) ||
    // iOS's own (non-standard) flag — display-mode media query support there is inconsistent
    // across versions, so check both.
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  )
}

// Chrome/Edge/Samsung Internet on Android and Chrome/Edge on desktop all fire
// `beforeinstallprompt` themselves — this is only consulted once that event never arrived, so it
// only needs to cover the browsers that don't support it: Safari (iOS/macOS), Firefox
// (everywhere), and anything else that reaches the generic fallback.
function getManualInstructions(): ManualInstallInstructions {
  if (isIosSafari()) {
    return {
      title: 'Add to your Home Screen',
      steps: 'Tap the Share icon in Safari’s toolbar, then choose "Add to Home Screen".',
      guide: {
        control: 'share',
        // iPhone Safari puts the toolbar at the bottom of the screen; iPad keeps it up top.
        location: isIpad() ? 'top-right' : 'bottom-bar',
        menuItem: 'Add to Home Screen',
        verb: 'Tap',
        where: isIpad() ? 'in the toolbar at the top of the screen' : 'in the bar at the bottom of the screen',
      },
    }
  }
  // Chrome/Firefox/Edge on iOS are WebKit underneath and can all add to the Home Screen through
  // the iOS share sheet — same gesture, but it isn't reached from "Safari's toolbar", so keep the
  // wording browser-neutral here rather than dropping through to the generic copy below.
  if (isIosDevice()) {
    return {
      title: 'Add to your Home Screen',
      steps: 'Tap the Share icon, then choose "Add to Home Screen".',
      guide: {
        control: 'share',
        location: isIpad() ? 'top-right' : 'bottom-bar',
        menuItem: 'Add to Home Screen',
        verb: 'Tap',
        where: isIpad() ? 'in the toolbar at the top of the screen' : 'in your browser’s toolbar',
      },
    }
  }
  // Safari performs a genuine PWA install via Add to Dock (macOS 14+), which is strictly better
  // than a .webloc — that only reopens a browser tab. The guide below therefore leads with the
  // real install, and the shortcut download is offered underneath it as a secondary action, for
  // the case where someone would rather have one click now than walk the menu. Safari lacks the
  // scripted *prompt*, not installation itself; nothing here can trigger Add to Dock directly.
  if (isMacSafari()) {
    return {
      title: 'Install this app',
      steps: 'Click the Share icon in Safari’s toolbar, then choose "Add to Dock" (or "Add to Home Screen" on older Safari versions).',
      guide: {
        control: 'share',
        location: 'top-right',
        menuItem: 'Add to Dock',
        verb: 'Click',
        where: 'in Safari’s toolbar, near the top right',
      },
      canDownloadShortcut: offerShortcutDownload(),
    }
  }
  if (isAndroid()) {
    return {
      title: 'Install this app',
      steps: 'Open your browser’s menu (⋮) and tap "Install app" or "Add to Home screen".',
      guide: {
        control: 'menu',
        location: 'top-right',
        menuItem: 'Install app',
        verb: 'Tap',
        where: 'at the top right of your browser',
      },
    }
  }
  // Reached on desktop Chrome/Edge only when `beforeinstallprompt` didn't arrive — the app is
  // already installed, the prompt was dismissed earlier this session, or the browser hasn't
  // judged the site installable yet. The manual route still works, so name it precisely.
  if (isChromiumDesktop()) {
    return {
      title: 'Install this app',
      steps: 'Click the install icon in the address bar, or open the browser menu (⋮) and choose "Install page as app…".',
    }
  }
  // Say so plainly rather than pointing at a menu item that does not exist — the previous
  // generic copy sent desktop Firefox users hunting for an "Install" option Firefox has never
  // had, which reads as the app being broken rather than the browser lacking the feature.
  // Verified against Firefox 153: `onbeforeinstallprompt` is not even present on `window`, but
  // the service worker registers and the app does load fully offline, so lead with what works.
  if (isFirefoxDesktop()) {
    return {
      title: 'Firefox can’t install web apps',
      steps: 'This page already works offline in Firefox — no install needed. To add it as a real desktop app, open it in Chrome, Edge, or Safari.',
      canDownloadShortcut: offerShortcutDownload(),
    }
  }
  return {
    title: 'Install this app',
    steps: 'If your browser supports installing web apps, you’ll find "Install" or "Add to Home Screen" in its menu. Chrome, Edge, and Safari all support it.',
    canDownloadShortcut: offerShortcutDownload(),
  }
}

/** Surfaces an install affordance for the PWA, on every browser/platform. Chrome/Edge/Samsung
 *  Internet (desktop and Android) fire `beforeinstallprompt`, so `canInstall`/`promptInstall`
 *  drive a real native install prompt there. Everywhere else (Safari on iOS/macOS, Firefox,
 *  anything else) never fires that event — there's no programmatic install — so
 *  `manualInstructions` carries platform-appropriate "how to install" copy instead, whenever the
 *  app isn't already installed (`isStandalone`). */
export function usePwaInstall() {
  // Seeded from the event index.html stashed before this bundle even parsed — by the time this
  // hook mounts, Chrome has usually already fired (and will never re-fire) `beforeinstallprompt`.
  const [deferredEvent, setDeferredEvent] = useState<BeforeInstallPromptEvent | null>(
    () => (typeof window === 'undefined' ? null : window.__deferredInstallPrompt ?? null)
  )

  useEffect(() => {
    function handleBeforeInstallPrompt(e: Event) {
      e.preventDefault()
      setDeferredEvent(e as BeforeInstallPromptEvent)
    }
    function handleAppInstalled() {
      window.__deferredInstallPrompt = null
      setDeferredEvent(null)
    }
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
    window.addEventListener('appinstalled', handleAppInstalled)
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
      window.removeEventListener('appinstalled', handleAppInstalled)
    }
  }, [])

  async function promptInstall() {
    if (!deferredEvent) return
    await deferredEvent.prompt()
    await deferredEvent.userChoice
    // A given beforeinstallprompt event can only be prompt()ed once — drop the stash too, or a
    // remount would seed itself with the spent event and render a button that no longer works.
    window.__deferredInstallPrompt = null
    setDeferredEvent(null)
  }

  const canInstall = !!deferredEvent
  const standalone = isStandalone()

  return {
    canInstall,
    promptInstall,
    isStandalone: standalone,
    manualInstructions: !canInstall && !standalone ? getManualInstructions() : null,
  }
}
